import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { getPastDate } from '@feathr/hooks';

import {
  clampCustomRange,
  type IDashboardUrlState,
  parseUrlState,
  presetToRange,
  serializeUrlState,
  type TDateRange,
  type TTimeframePreset,
} from './ReportsConversionsDashboardPage.utils';

export interface IDashboardFilterUpdate {
  attr: IDashboardUrlState['attr'];
  projects: string[];
  types: string[];
}

export interface IUseDashboardUrlStateReturn {
  state: IDashboardUrlState;
  range: TDateRange;
  setPreset: (preset: TTimeframePreset) => void;
  setCustomRange: (range: TDateRange) => void;
  setFilters: (update: IDashboardFilterUpdate) => void;
  setArchived: (archived: boolean) => void;
}

const CUSTOM_RANGE_DEFAULT_DAYS = 30;

/**
 * Owns the Conversions Dashboard's URL state: timeframe (with custom range
 * support), attribution model, projects, campaign types, archived-goals toggle.
 *
 * Preset → date math lives in `presetToRange`. The dashboard owns its own
 * preset list (see TimeframeControls) so its 7d / calendar-anchored / custom
 * options never leak into other surfaces that share the cross-app TimeFrameSelect.
 */
export function useDashboardUrlState(): IUseDashboardUrlStateReturn {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo(() => parseUrlState(location.search), [location.search]);

  const range = useMemo<TDateRange>(() => {
    const customRange: TDateRange = [state.start ?? '', state.end ?? ''];
    return presetToRange(state.timeframe, customRange);
  }, [state.timeframe, state.start, state.end]);

  const writeUrl = useCallback(
    (next: Partial<IDashboardUrlState>): void => {
      const current = parseUrlState(history.location.search);
      const merged = { ...current, ...next };
      const search = serializeUrlState(merged);
      history.replace({
        ...history.location,
        search: search ? `?${search}` : '',
      });
    },
    [history],
  );

  const setPreset = useCallback(
    (preset: TTimeframePreset): void => {
      if (preset === 'custom') {
        const today = new Date().toISOString().split('T')[0];
        const start = getPastDate({ amount: CUSTOM_RANGE_DEFAULT_DAYS, unit: 'days' });
        writeUrl({ timeframe: 'custom', start, end: today });
        return;
      }
      writeUrl({ timeframe: preset, start: undefined, end: undefined });
    },
    [writeUrl],
  );

  const setCustomRange = useCallback(
    ([start, end]: TDateRange): void => {
      const clamped = clampCustomRange(start, end);
      writeUrl({ timeframe: 'custom', start: clamped.start, end: clamped.end });
    },
    [writeUrl],
  );

  const setFilters = useCallback(
    ({ attr, projects, types }: IDashboardFilterUpdate): void =>
      writeUrl({ attr, projects, types }),
    [writeUrl],
  );
  const setArchived = useCallback((archived: boolean): void => writeUrl({ archived }), [writeUrl]);

  return {
    state,
    range,
    setPreset,
    setCustomRange,
    setFilters,
    setArchived,
  };
}
